import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface TopPost {
  id: string;
  content: string;
  created_at: string;
  user: {
    id: string;
    full_name: string;
    profile_picture_url?: string;
    username?: string;
    country?: string;
    belt_rank?: string;
    academy_name?: string;
    academy_city?: string;
  };
  // TODO: Add engagement metrics when we implement likes/comments
  // likes_count: number;
  // comments_count: number;
}

export const useTopPosts = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['top-posts', user?.id],
    queryFn: async (): Promise<TopPost[]> => {
      if (!user) return [];

      try {
        // For now, get the most recent public posts
        // TODO: When we implement likes/comments, prioritize by engagement
        const { data, error } = await supabase
          .from('posts')
          .select(`
            id,
            content,
            created_at,
            visibility,
            user_id
          `)
          .eq('visibility', 'public')
          .order('created_at', { ascending: false })
          .limit(5);

        if (error) {
          console.error('Error fetching posts:', error);
          throw error;
        }

        // Get user profiles for the posts
        const userIds = [...new Set(data?.map(post => post.user_id) || [])];
        
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select(`
            id,
            full_name,
            profile_picture_url,
            username,
            country,
            belt_rank,
            main_academy_id
          `)
          .in('id', userIds);

        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
          throw profilesError;
        }

        // Get academy information for the profiles
        const academyIds = [...new Set(profiles?.filter(p => p.main_academy_id).map(p => p.main_academy_id) || [])];
        const { data: academies, error: academiesError } = await supabase
          .from('academies')
          .select(`
            id,
            name,
            city
          `)
          .in('id', academyIds);

        if (academiesError) {
          console.error('Error fetching academies:', academiesError);
          throw academiesError;
        }

        // Create a map of academies
        const academiesMap = new Map(academies?.map(academy => [academy.id, academy]) || []);

        // Create a map of user profiles
        const profilesMap = new Map(profiles?.map(profile => [profile.id, profile]) || []);

        // Transform the data to include user information
        const posts = data?.map(post => {
          const userProfile = profilesMap.get(post.user_id);
          const academy = userProfile?.main_academy_id ? academiesMap.get(userProfile.main_academy_id) : null;
          
          return {
            id: post.id,
            content: post.content,
            created_at: post.created_at,
            user: {
              id: userProfile?.id || post.user_id,
              full_name: userProfile?.full_name || 'Anonymous',
              profile_picture_url: userProfile?.profile_picture_url,
              username: userProfile?.username,
              country: userProfile?.country,
              belt_rank: userProfile?.belt_rank || 'white',
              academy_name: academy?.name,
              academy_city: academy?.city,
            },
          };
        }) || [];

        return posts;
      } catch (error) {
        console.error('Error in useTopPosts:', error);
        throw error;
      }
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
